import type { ReactNode } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

interface Props {
  title?: string
  /** One line under the title. Omitted more often than not. */
  description?: string
  /** A control that belongs to this section rather than to the page. */
  action?: ReactNode
  /** Removes the inner padding, for a card whose whole body is a table. */
  flush?: boolean
  children: ReactNode
}

/**
 * One titled block of a page.
 *
 * Every surface in this portal was a `<Paper variant="outlined" sx={{ p: 3 }}>`
 * written out again at each call site, and they had drifted: three paddings,
 * two heading levels and a gap under the title that was sometimes there. The
 * drift is invisible in any single screenshot and obvious across four.
 *
 * The rhythm here is the console's — same radius, same border, same 3-unit
 * padding — so the two products read as one family without the portal
 * borrowing the console's density.
 */
export function SectionCard({ title, description, action, flush = false, children }: Props) {
  return (
    <Paper variant="outlined" sx={{ p: flush ? 0 : 3, overflow: 'hidden' }}>
      {title ? (
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            mb: description ? 1 : 2,
            ...(flush ? { p: 3, pb: description ? 1 : 2 } : {}),
          }}
        >
          <Typography variant="h2">{title}</Typography>
          {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
        </Stack>
      ) : null}
      {description ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, maxWidth: 760, ...(flush ? { px: 3 } : {}) }}
        >
          {description}
        </Typography>
      ) : null}
      {children}
    </Paper>
  )
}
